import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import SSOContext from 'contexts/sso/context';
import { useShare } from 'contexts/share/use_share';
import { useCurrentBrand } from 'contexts/current_brand/use_current_brand';
import { useSession } from 'hooks/use_context';
import { PROTOCOL, APP_DOMAIN, API_DOMAIN } from 'constants';
import { setAuthorizationToken } from 'services/authorization_token';
import Loader from 'components/ui/loader';

const SSOProvider = ({ children }) => {
  const popupRef = useRef(null);
  const intervalRef = useRef(null);
  const { currentUser } = useSession();
  const { inShare, autoSignIn } = useShare();
  const [SSOConnectionError, setSSOConnectionError] = useState(null);
  const { currentBrand } = useCurrentBrand();
  const { fetchSession } = useSession();
  const signingIn = autoSignIn && !currentUser;

  const IGDomain = `${PROTOCOL}://${APP_DOMAIN}`;

  const SSOUrl = currentBrand && `${PROTOCOL}://${API_DOMAIN}/sso/authorization?brand=${currentBrand.slug}${inShare ? '&embed=true' : ''}`;

  const closePopup = () => {
    intervalRef.current && clearInterval(intervalRef.current);
    popupRef.current && popupRef.current.close();
  };

  const openSSOPopup = (newUrl) => {
    popupRef.current = window.open(newUrl, 'popup', 'popup=true,width=500,height=600');
    window.addEventListener('message', (event) => {
      if (event.origin === IGDomain && typeof event.data === 'string') {
        const response = JSON.parse(event.data);
        if (response.type === 'error') {
          setSSOConnectionError(response.message);
          closePopup();
        }
        if (response.type === 'success') {
          setAuthorizationToken(response.data.authorizationToken);
          fetchSession();
          closePopup();
        }
      }
    });
    intervalRef.current = setInterval(() => {
      popupRef.current.postMessage('Are you connected ?', IGDomain);
    }, 1000);
  };

  const signInWithSSO = (customParams = {}) => {
    const newUrl = `${SSOUrl}&${new URLSearchParams(customParams).toString()}`;
    if (inShare) {
      return openSSOPopup(newUrl);
    }
    window.location.href = newUrl;
  };

  useEffect(() => {
    if (signingIn) {
      signInWithSSO();
    }
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        closePopup();
        popupRef.current = null;
      }
    };
  });

  if (signingIn) {
    return <Loader />;
  }

  return (
    <SSOContext.Provider
      value={{
        signInWithSSO,
        SSOConnectionError,
      }}
    >
      {children}
    </SSOContext.Provider>
  );
};

SSOProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]).isRequired,
};

export default SSOProvider;
